import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useReviewActions } from "../../hook/Usereviewgarage";
import ReviewActionDialog from "./Reviewactiondialog";

const getReviewStatusClass = (status) => {
  switch (status) {
    case "visible":
      return "bg-success-focus text-success-600 border-success-main";
    case "removed":
      return "bg-danger-focus text-danger-600 border-danger-main";
    case "flagged":
      return "bg-warning-focus text-warning-600 border-warning-main";
    default:
      return "bg-warning-focus text-warning-600 border-warning-main";
  }
};

const timelineDotColor = {
  posted: "#8a8f9c",
  visible: "#198754",
  approved: "#198754",
  flagged: "#f59e0b",
  removed: "#dc3545",
  rejected: "#dc3545",
};

const formatDate = (date, dateFormat = "dd MMM, yyyy hh:mm a") => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "-";

  return format(parsedDate, dateFormat);
};

const ReviewTimeline = ({ events = [] }) => {
  const sorted = [...events].sort((a, b) => new Date(b.at) - new Date(a.at));

  if (sorted.length === 0) {
    return <p className="text-secondary-light mb-0">No history yet.</p>;
  }

  return (
    <ul className="list-unstyled mb-0 position-relative">
      <span
        style={{
          position: "absolute",
          left: 5,
          top: 8,
          bottom: 8,
          width: 2,
          background: "#dee2e6",
        }}
      />

      {sorted.map((event, index) => (
        <li
          key={`${event.action}-${event.at}-${index}`}
          className={index === sorted.length - 1 ? "mb-0" : "mb-16"}
          style={{ position: "relative", paddingLeft: 28 }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              top: 5,
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: timelineDotColor[event.action] || "#8a8f9c",
              boxShadow: "0 0 0 3px #fff",
            }}
          />

          <div className="fw-semibold">
            {event.label || event.action || "Review activity"}
          </div>

          <small className="d-block text-secondary-light">
            {formatDate(event.at, "dd MMM, yyyy · hh:mm a")}
            {" · "}
            {event.actor?.name || "System"}
          </small>

          {event.reason && (
            <small className="d-block">Reason: {event.reason}</small>
          )}

          {event.note && <small className="d-block">{event.note}</small>}
        </li>
      ))}
    </ul>
  );
};

const GarageReviewDetailLayer = () => {
  const { id } = useParams();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(0);

  // Remove dialog state: { reasons } or null
  const [removeDialog, setRemoveDialog] = useState(null);

  const { actionLoadingId, removeReview, fetchRemovalReasons } =
    useReviewActions();

  const removing = actionLoadingId != null;

  // Fetch review details directly from the API.
  useEffect(() => {
    let cancelled = false;

    const loadReview = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("token");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/reviews/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        const payload = await response.json();

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              payload?.errorMessage ||
              "Failed to load review details.",
          );
        }

        const reviewData = payload?.data ?? payload;

        if (!cancelled) {
          setReview(reviewData?.id ? reviewData : null);
        }
      } catch (error) {
        console.error("Error loading review:", error);

        if (!cancelled) {
          setReview(null);
          toast.error(error?.message || "Failed to load review details.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadReview();

    return () => {
      cancelled = true;
    };
  }, [id, update]);

  // Same remove flow as the all-reviews list: fetch reasons, then open dialog.
  const handleOpenRemove = async () => {
    try {
      const reasons = await fetchRemovalReasons(id);

      if (reasons.length === 0) {
        toast.error("No removal reasons were returned by the API.");
        return;
      }

      setRemoveDialog({ reasons });
    } catch (error) {
      console.error("Error fetching removal reasons:", error);
      toast.error(error?.message || "Failed to fetch removal reasons.");
    }
  };

  const handleConfirmRemove = async ({ reason, note }) => {
    try {
      await removeReview(id, reason, note);

      toast.success("Review removed.");
      setRemoveDialog(null);
      setUpdate((current) => current + 1);
    } catch (error) {
      console.error("Error removing review:", error);
      toast.error(error?.message || "Failed to remove review.");
    }
  };

  if (loading && !review) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center py-5">Loading...</div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center py-5">
          <p className="mb-16">Review not found.</p>

          <Link to="/garage-reviews" className="btn btn-sm btn-primary-600">
            Back to reviews
          </Link>
        </div>
      </div>
    );
  }

  const rating = Math.max(
    0,
    Math.min(5, Math.round(Number(review.rating) || 0)),
  );

  const moderation = review.moderation || {};
  const status = moderation.status || "visible";

  const comment = review.review_text || "";
  const reviewer = review.reviewer || {};
  const garage = review.garage || {};
  const booking = review.booking || {};
  const vehicle = booking.vehicle || {};

  const timelineEvents = Array.isArray(review.timeline) ? review.timeline : [];

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <h6 className="mb-0">Review #{review.id}</h6>

        <div className="d-flex flex-wrap align-items-center gap-2">
          {status !== "removed" && (
            <button
              type="button"
              className="btn btn-sm btn-danger d-inline-flex align-items-center gap-1"
              onClick={handleOpenRemove}
              disabled={removing}
            >
              <Icon icon="fluent:delete-24-regular" />
              {removing && !removeDialog ? "Please wait..." : "Remove"}
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        <div className="row g-4">
          {/* Review content */}
          <div className="col-12 col-lg-8">
            <div className="border radius-8 p-24 mb-24">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-16">
                <div className="text-warning fs-4 text-nowrap">
                  {"★".repeat(rating)}

                  <span className="text-secondary-light">
                    {"★".repeat(5 - rating)}
                  </span>

                  <span className="text-secondary-light fs-6 ms-2">
                    {review.rating ?? 0} / 5
                  </span>
                </div>

                <span
                  className={`border px-16 py-4 radius-4 fw-medium text-sm text-capitalize ${getReviewStatusClass(
                    status,
                  )}`}
                >
                  {status === "visible" ? "Published" : status}
                </span>
              </div>

              <p
                className="text-secondary-light mb-16"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {comment || "No comment provided."}
              </p>

              {review.app_rating != null && (
                <div className="mb-16">
                  <span className="text-secondary-light me-2">App Rating:</span>

                  <span className="text-warning">
                    {"★".repeat(
                      Math.max(
                        0,
                        Math.min(5, Math.round(Number(review.app_rating) || 0)),
                      ),
                    )}
                  </span>

                  <span className="text-secondary-light ms-2">
                    {review.app_rating} / 5
                  </span>
                </div>
              )}

              {review.app_review_text && (
                <div className="mb-16">
                  <h6 className="text-sm mb-8">App Review</h6>

                  <p
                    className="text-secondary-light mb-0"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {review.app_review_text}
                  </p>
                </div>
              )}

              {status === "flagged" && moderation.flag_reason && (
                <div className="alert alert-warning mb-16" role="alert">
                  <strong>Flag reason:</strong> {moderation.flag_reason}
                </div>
              )}

              {status === "removed" && (
                <div className="alert alert-danger mb-16" role="alert">
                  <strong>Removal reason:</strong>{" "}
                  {moderation.removal_reason_label ||
                    moderation.removal_reason ||
                    "No removal reason provided."}
                  {moderation.removed_by != null && (
                    <div className="mt-8">
                      <strong>Removed by:</strong>{" "}
                      {typeof moderation.removed_by === "object"
                        ? moderation.removed_by.name ||
                          moderation.removed_by.fullName ||
                          moderation.removed_by.id
                        : moderation.removed_by}
                    </div>
                  )}
                  {moderation.removed_at && (
                    <div className="mt-8">
                      <strong>Removed on:</strong>{" "}
                      {formatDate(moderation.removed_at)}
                    </div>
                  )}
                </div>
              )}

              {moderation.internal_note && (
                <div className="alert alert-light border mb-16" role="alert">
                  <strong>Internal note:</strong> {moderation.internal_note}
                </div>
              )}

              <hr />

              <small className="text-secondary-light">
                Submitted on {formatDate(review.created_at)}
              </small>
            </div>

            {/* Booking Details moved before Timeline */}
            {review.booking && (
              <div className="border radius-8 p-24 mb-24">
                <h6 className="mb-16">Booking Details</h6>

                <ul className="list-unstyled mb-0">
                  <li className="d-flex justify-content-between mb-8 gap-3">
                    <span className="text-secondary-light">Booking ID</span>
                    <span>{booking.id ?? review.booking_id ?? "-"}</span>
                  </li>

                  <li className="d-flex justify-content-between mb-8 gap-3">
                    <span className="text-secondary-light">Booking Status</span>
                    <span className="text-capitalize">
                      {booking.status || "-"}
                    </span>
                  </li>

                  <li className="d-flex justify-content-between mb-8 gap-3">
                    <span className="text-secondary-light">Booking Date</span>
                    <span className="text-end">
                      {booking.booking_date
                        ? formatDate(booking.booking_date, "dd MMM, yyyy")
                        : "-"}
                    </span>
                  </li>

                  <li className="d-flex justify-content-between mb-8 gap-3">
                    <span className="text-secondary-light">Vehicle</span>
                    <span className="text-end">
                      {[vehicle.brand, vehicle.model]
                        .filter(Boolean)
                        .join(" ") || "-"}
                    </span>
                  </li>

                  <li className="d-flex justify-content-between mb-8 gap-3">
                    <span className="text-secondary-light">Year</span>
                    <span>{vehicle.year ?? "-"}</span>
                  </li>

                  <li className="d-flex justify-content-between gap-3">
                    <span className="text-secondary-light">License Plate</span>
                    <span>{vehicle.licensePlate || "-"}</span>
                  </li>
                </ul>
              </div>
            )}

            {/* Timeline now appears after Booking Details */}
            <div className="border radius-8 p-24">
              <h6 className="mb-16">Timeline</h6>
              <ReviewTimeline events={timelineEvents} />
            </div>
          </div>

          {/* Reviewer + Garage info */}
          <div className="col-12 col-lg-4">
            <div className="border radius-8 p-24 mb-24">
              <h6 className="mb-16">Reviewer</h6>

              <div className="d-flex align-items-center mb-12">
                <div>
                  <div className="fw-medium">
                    {reviewer.fullName || reviewer.username || "-"}
                  </div>

                  <small className="text-secondary-light d-block">
                    {reviewer.email || ""}
                  </small>

                  {reviewer.phoneNumber && (
                    <small className="text-secondary-light d-block">
                      {reviewer.phoneNumber}
                    </small>
                  )}
                </div>
              </div>

              {reviewer.id != null && (
                <div className="d-flex justify-content-between mb-8">
                  <span className="text-secondary-light">Reviewer ID</span>
                  <span>{reviewer.id}</span>
                </div>
              )}
            </div>

            <div className="border radius-8 p-24 mb-24">
              <h6 className="mb-16">Garage</h6>

              <ul className="list-unstyled mb-16">
                <li className="d-flex justify-content-between mb-8 gap-3">
                  <span className="text-secondary-light">Name</span>
                  <span className="text-end">{garage.garage_name || "-"}</span>
                </li>

                <li className="d-flex justify-content-between mb-8 gap-3">
                  <span className="text-secondary-light">Garage ID</span>
                  <span>{garage.id ?? "-"}</span>
                </li>
              </ul>

              {garage.id && (
                <Link
                  to={`/garage-details/${garage.id}`}
                  className="btn btn-sm btn-outline-primary w-100"
                >
                  View garage
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <ReviewActionDialog
        isOpen={!!removeDialog}
        type="remove"
        reasons={removeDialog?.reasons || []}
        loading={removing}
        onClose={() => !removing && setRemoveDialog(null)}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default GarageReviewDetailLayer;
